package dev.lingvo.site.components.marketing

/**
 * The one key the whole marketing site argues about.
 *
 * Every surface that shows a string shows *this* string: the plurals band, its instrument, and the
 * CLI output beside them. That is deliberate — a reader who meets `checkout.items_count` in the hero
 * and again in the terminal is being shown one thing from three angles, not three examples. Two keys
 * would make it a gallery.
 *
 * `many` is null on purpose. Russian requires four categories and this key has three, which is the
 * failure the page exists to demonstrate; a complete example would prove nothing.
 *
 * It lives in its own file because it outlived the component it was declared in. It was previously
 * inside `ProductPreview.kt`, which the Polyglot redesign replaced — and deleting that file took the
 * data with it, which is how a constant shared by three composables comes to be defined inside one
 * of them.
 *
 * **`SOURCE`, `MISSING`, `SHORTFALL` and `QA_FAILURE` were the same story a second time.** Their one
 * remaining reader was `PluralInstrument`, which nothing called; two of them also built an English
 * plural by hand — `if (MISSING.size == 1) "category" else "categories"` — in the one file on this
 * site whose entire subject is that a language may need four forms and no `if` can choose between
 * them. They are gone rather than converted, because `tPlural` cannot rescue a string no reader ever
 * sees. Anything that needs them again should reach for `tPlural` on the way back in.
 */
internal object ProofKey {
    const val NAME = "checkout.items_count"

    /** The CLDR categories Russian requires, in order. A null value is one not translated yet. */
    val RU_PLURALS: List<Pair<String, String?>> = listOf(
        "one" to "{count} товар",
        "few" to "{count} товара",
        "many" to null,
        "other" to "{count} товара",
    )
}
